import React from 'react';
import LandingStory from '../components/home/LandingStory';
import SEO from '../components/common/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import FloatingBackground from '../components/home/FloatingBackground';
import { motion } from 'framer-motion';

const Section = ({ children, className = "" }) => (
  <section className={`py-16 md:py-24 px-4 ${className}`}>
    <div className="max-w-5xl mx-auto">
      {children}
    </div>
  </section>
);

export default function LandingPage() {
  const { t } = useLanguage();

  return (
    <div className="relative min-h-screen overflow-hidden">
      <SEO 
        title={t('aboutUs', 'About Us')}
        description={t('landing.conceptDesc')}
      />
      <FloatingBackground />
      
      {/* Decorative Glows */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 blur-[120px] rounded-full -z-10" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-secondary/5 blur-[100px] rounded-full -z-10" />

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative z-10"
      >
        <LandingStory isPage={true} />
      </motion.div>

      {/* Vision Section */}
      <Section className="pb-32">
         <div className="grid md:grid-cols-2 gap-12 items-center bg-card/30 backdrop-blur-xl rounded-[4rem] p-8 md:p-20 border border-white/10 shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-[8rem] group-hover:bg-primary/10 transition-colors duration-700" />
            
            <div className="space-y-8 relative z-10">
                <div className="w-16 h-1 bg-primary rounded-full" />
                <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-none">
                    {t('ourVision', 'Our Vision')}
                </h2>
                <p className="text-xl text-muted-foreground leading-relaxed font-medium">
                    {t('visionDesc')}
                </p>
                <div className="flex flex-wrap gap-4 pt-4">
                   <div className="p-4 bg-background/50 rounded-2xl border border-border shadow-sm flex-1 min-w-[100px]">
                      <p className="text-2xl font-black text-primary">100%</p>
                      <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">{t('secure')}</p>
                   </div>
                   <div className="p-4 bg-background/50 rounded-2xl border border-border shadow-sm flex-1 min-w-[100px]">
                      <p className="text-2xl font-black text-primary">∞</p>
                      <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">{t('community')}</p>
                   </div>
                   <div className="p-4 bg-background/50 rounded-2xl border border-border shadow-sm flex-1 min-w-[100px]">
                      <p className="text-2xl font-black text-primary">♻️</p>
                      <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">{t('sustainability')}</p>
                   </div>
                </div>
            </div>
            
            <div className="relative aspect-square rounded-[3rem] overflow-hidden shadow-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center border border-white/10 group-hover:scale-[1.02] transition-transform duration-700">
                <motion.div 
                  animate={{ 
                    y: [0, -20, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="text-9xl filter drop-shadow-2xl"
                >
                   🌍
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
         </div>
      </Section>
    </div>
  );
}
